import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';

import { type LearnTask } from '@apps/web/features/learn_phoebe/model/learn_content';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { EVENTS } from '@apps/web/lib/events';

/**
 * Product analytics for Learn Phoebe.
 *
 * The whole feature ships unflagged to every org, so "is anyone using this, and
 * did it move chat usage" has to be answerable from events alone. One hook owns
 * the payload contract so the page and the sidebar checklist can never describe
 * the same interaction differently.
 *
 * Every capture is best-effort: analytics must never break the surface it
 * measures.
 */

// Which Learn Phoebe surface the interaction happened on. The same task can be
// opened from either, and the two answer different questions (did the page work
// vs did the sidebar nudge work), so every event carries it.
export type LearnPhoebeSurface = 'page' | 'checklist_dialog';

type TaskEvent = {
    readonly surface: LearnPhoebeSurface;
    readonly taskId: LearnTask['id'];
    // Null on the page: only the checklist tracks steps.
    readonly stepId: string | null;
};

type ChecklistOpenedEvent = {
    readonly completedCount: number;
    readonly totalSteps: number;
    // The dialog auto-selects the first incomplete step without a click, so
    // that step never fires a task-opened event. Carry it here instead.
    readonly defaultStepId: string;
};

type LearnPhoebeAnalytics = {
    readonly captureTaskOpened: (event: TaskEvent) => void;
    readonly captureTryInChatClicked: (event: TaskEvent) => void;
    readonly capturePromptCopied: (event: TaskEvent) => void;
    readonly captureChecklistOpened: (event: ChecklistOpenedEvent) => void;
};

export function useLearnPhoebeAnalytics(): LearnPhoebeAnalytics {
    const posthog = usePostHog();
    const organizationId = useOrganizationId();

    function capture(
        eventName: string,
        properties: Record<string, unknown>,
    ): void {
        try {
            posthog?.capture(eventName, {
                organization_id: organizationId,
                ...properties,
            });
        } catch {
            // Best-effort: a failed capture must not break the interaction.
        }
    }

    function taskProperties(event: TaskEvent): Record<string, unknown> {
        return {
            surface: event.surface,
            task_id: event.taskId,
            step_id: event.stepId,
        };
    }

    return {
        captureTaskOpened: (event) => {
            capture(EVENTS.LEARN_PHOEBE_TASK_OPENED, taskProperties(event));
        },
        captureTryInChatClicked: (event) => {
            capture(
                EVENTS.LEARN_PHOEBE_TRY_IN_CHAT_CLICKED,
                taskProperties(event),
            );
        },
        capturePromptCopied: (event) => {
            capture(EVENTS.LEARN_PHOEBE_PROMPT_COPIED, taskProperties(event));
        },
        captureChecklistOpened: (event) => {
            capture(EVENTS.LEARN_PHOEBE_CHECKLIST_OPENED, {
                completed_count: event.completedCount,
                total_steps: event.totalSteps,
                default_step_id: event.defaultStepId,
            });
        },
    };
}

/**
 * Fires once per mount.
 *
 * An explicit event, not `$pageview`: PostHog runs with the legacy pageview
 * behavior (a pageview only on initial document load), and the sidebar entry is
 * a client-side route link — so the way almost everyone reaches this page emits
 * no pageview at all.
 */
export function useLearnPhoebePageViewed(): void {
    const posthog = usePostHog();
    const organizationId = useOrganizationId();
    const capturedForOrganizationRef = useRef<string | null>(null);

    useEffect(() => {
        if (
            organizationId === null ||
            capturedForOrganizationRef.current === organizationId
        ) {
            return;
        }
        capturedForOrganizationRef.current = organizationId;
        try {
            posthog?.capture(EVENTS.LEARN_PHOEBE_PAGE_VIEWED, {
                organization_id: organizationId,
            });
        } catch {
            // Best-effort.
        }
    }, [organizationId, posthog]);
}
